import React, { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  useStripe,
  useElements,
  CardNumberElement,
  Elements,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';
import { CashPaymentOption, Event, MultiAppointmentResponse, PaymentDetails } from '../types';

import { showNotification } from '@/utils/notificationUtils';
import { useAuth } from '@/lib/auth';
import { CreditCard } from '@/features/billing/api/upgradePlan';
import './MakePaymentStripe.scss';
import { PaymentCards } from './PaymentCards';
import { Button } from '@/components/Elements';
import { AppointmentPreview } from './AppointmentPreview';
import { PaymentOptions } from './PaymentOptions';
import Utils from '@/utils/util';
import { UserContext } from '@/routes';
import { useTranslation } from 'react-i18next';
import { MultiAppointmentPreview } from './MultiAppointmentPreview';
const useOptions = () => {
  const userContext = useContext(UserContext);
  const options = useMemo(
    () => ({
      style: {
        width: '100%',
        base: {
          width: '100%',
          color: '#1a1a1a',
          letterSpacing: '0.025em',
          fontFamily: 'Inter, sans-serif',
          fontSize: userContext?.isLandscape ? '1.125rem' : '16px',
          fontWeight: '500',
          '::placeholder': {
            color: '#aab7c4',
            fontFamily: 'Inter, sans-serif',
          },
        },
        invalid: {
          color: '#9e2146',
        },
      },
    }),
    []
  );

  return options;
};
type Props = {
  appointment?: Event;
  multiAppointmentDetails?: MultiAppointmentResponse;
  totalAmount?: number;
  onMakePayment: (data?: PaymentDetails) => void;
  onBackClick: () => void;
  paymentDetails?: PaymentDetails;
  onChange: (paymentDetails: PaymentDetails) => void;
  isLoading: boolean;
};

export const MakePaymentStripe = ({
  onMakePayment,
  totalAmount,
  appointment,
  multiAppointmentDetails,
  paymentDetails,
  onBackClick,
  onChange,
  isLoading,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const options = useOptions();
  const [cardName, setCardName] = useState('');
  const [isStripeProcessing, setIsStripeProcessing] = useState<boolean>(false);
  useEffect(() => {
    Utils.scrollToTop();
  }, []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.id === 'name') {
      setCardName(event.target.value);
    }
  };

  const createStripeTokenForCard = async () => {
    if (!elements || !stripe) {
      return;
    }
    if (!user?.activeLocation?.isStripeAccountVerified) {
      onMakePayment();
      return;
    }
    const cardNumberElement: any = elements.getElement(CardNumberElement);
    const cardExpiryElement: any = elements.getElement(CardExpiryElement);
    const cardCvcElement: any = elements.getElement(CardCvcElement);
    if (
      cardNumberElement['_empty'] &&
      cardExpiryElement['_empty'] &&
      cardCvcElement['_empty'] &&
      (!cardName || cardName.trim().length < 1)
    ) {
      onMakePayment();
      return;
    }
    if (!cardName || cardName.trim().length < 1) {
      showNotification({
        type: 'error',
        title: t('Please enter name on the card'),
      });
      return;
    } else if (cardNumberElement['_invalid']) {
      showNotification({
        type: 'error',
        title: t('Invalid card details , please enter valid card details.'),
      });
      return;
    } else if (!cardNumberElement['_complete']) {
      showNotification({
        type: 'error',
        title: t('Please complete filling card details.'),
      });
      return;
    } else if (cardExpiryElement['_invalid']) {
      showNotification({
        type: 'error',
        title: t('Invalid expiry details , please enter valid card expiry details.'),
      });
      return;
    } else if (!cardExpiryElement['_complete']) {
      showNotification({
        type: 'error',
        title: t('Please complete filling card expiry details.'),
      });
      return;
    } else if (cardCvcElement['_invalid']) {
      showNotification({
        type: 'error',
        title: t('Invalid cvv details , please enter valid cvv'),
      });
      return;
    } else if (!cardCvcElement['_complete']) {
      showNotification({
        type: 'error',
        title: t('Please complete filling cvv details.'),
      });
      return;
    }
    try {
      setIsStripeProcessing(true);
      const payload: any = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: cardName,
        },
      } as any);
      if (payload && payload.error) {
        showNotification({
          type: 'error',
          title: payload.error.message,
        });
      } else {
        const { paymentMethod } = payload;
        const paymentCard: CreditCard = {
          id: paymentMethod.id,
          expireMonth: paymentMethod.card.exp_month,
          expireYear: paymentMethod.card.exp_year,
          firstName: paymentMethod.billing_details.name,
          lastName: '',
          isDefault: true,
          number: paymentMethod.card.last4,
          type: paymentMethod.card.brand,
          stripePaymentToken: paymentMethod.id,
          squareCardId: '',
        };
        const pd: PaymentDetails = {
          data: paymentCard,
          type: 'card',
          paymentMode: '',
          isNewCard: true,
        };
        onMakePayment(pd);
      }
    } catch (error: any) {
      setIsStripeProcessing(false);
      showNotification({
        type: 'error',
        title: error ? error.message : '',
      });
    }
  };
  const onCashPaymentSelect = (cpo: CashPaymentOption) => {
    const pd: PaymentDetails = paymentDetails
      ? paymentDetails
      : { data: undefined, type: 'cash', paymentMode: '' };
    if (pd) {
      pd.data = cpo;
      pd.type = 'cash';
      onChange({ ...pd });
      setTimeout(() => {
        user?.activeLocation?.isStripeAccountVerified && clearStripeElementValues();
      }, 0);
    }
  };
  const clearStripeElementValues = () => {
    if (!elements || !stripe) {
      return;
    }
    const cardNumberElement: any = elements.getElement(CardNumberElement);
    const cardExpiryElement: any = elements.getElement(CardExpiryElement);
    const cardCvcElement: any = elements.getElement(CardCvcElement);
    cardNumberElement.clear();
    cardExpiryElement.clear();
    cardCvcElement.clear();
    setCardName('');
  };
  const onCardSelect = (cc: CreditCard) => {
    const pd: PaymentDetails = paymentDetails
      ? paymentDetails
      : { data: undefined, type: 'cash', paymentMode: '' };

    if (pd) {
      pd.data = cc;
      pd.type = 'card';
      onChange({ ...pd });
      setTimeout(() => {
        user?.activeLocation?.isStripeAccountVerified && clearStripeElementValues();
      }, 0);
    }
  };
  const onNewCardChange = () => {
    if (paymentDetails?.data) {
      onChange({ data: undefined, type: 'cash', paymentMode: '' });
    }
  };
  return (
    <>
      <div className="make-payment-stripe">
        {multiAppointmentDetails ? (
          <MultiAppointmentPreview
            multiAppointmentDetails={multiAppointmentDetails}
            totalAmount={totalAmount}
          />
        ) : (
          appointment && (
            <AppointmentPreview selectedAppointment={appointment} totalAmount={totalAmount} />
          )
        )}
        <PaymentCards
          customerId={appointment?.petParentId}
          paymentDetails={paymentDetails}
          onSelect={onCardSelect}
        />
        {user?.activeLocation?.isStripeAccountVerified && (
          <div className="credit-card-details">
            <label>{t('New credit card')}</label>
            <div className="credit-card-form">
              <div className="payment-input-field">
                <div>
                  <CardNumberElement options={options} onFocus={() => onNewCardChange()} />
                </div>
              </div>
              <div>
                <input
                  id="name"
                  type="text"
                  value={cardName}
                  className="payment-input-field"
                  onChange={handleChange}
                  placeholder={t('Name on the Card')}
                />
              </div>
              <div className="split">
                <div className="payment-input-field">
                  <div>
                    <CardExpiryElement options={options} onFocus={() => onNewCardChange()} />
                  </div>
                </div>
                <div className="payment-input-field">
                  <div>
                    <CardCvcElement options={options} onFocus={() => onNewCardChange()} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <PaymentOptions paymentDetails={paymentDetails} onSelect={onCashPaymentSelect} />
      </div>
      <div className="credit-card-form-action">
        <Button variant="nobg" size="lg" onClick={onBackClick} disabled={isLoading}>
          {t('Back')}
        </Button>
        <Button
          size="lg"
          onClick={createStripeTokenForCard}
          disabled={isLoading || isStripeProcessing}
        >
          {t('Make Payment')}
        </Button>
      </div>
    </>
  );
};
